/** Copilot maps these gateway tokens to UI actions — never sent as chat prompts. */
export const GATEWAY_CHIP_OPEN_SCOPE = '__open_scope__';
export const GATEWAY_CHIP_RETRY_LAST = '__retry_last__';

export type CopilotMessageRole = 'user' | 'assistant';

/** Per-request Time Doctor MCP config so the agent can query live workspace data. */
export interface CopilotMcpConfig {
  url: string;
  apiKey: string;
}

/** Connection state of Tempo's live Time Doctor workspace data (MCP). */
export type CopilotLiveDataStatus = 'disconnected' | 'connecting' | 'connected';

/** Response `actions[]` are rendered as chips. Shape is defensive: backend is still evolving. */
export interface CopilotAction {
  type?: string;
  label?: string;
  /** Navigation target (router URL) when the action navigates. */
  route?: string;
  /** Query params passed alongside `route` when navigating. */
  params?: Record<string, unknown>;
  /** Prompt to send when the action is a follow-up question. */
  prompt?: string;
  /** Optional Material icon name shown on the chip (e.g. navigation chips). */
  icon?: string;
  [key: string]: unknown;
}

/** A curated "Learn more" link (Help Center article) returned by the Agent Gateway. */
export interface HelpLink {
  title: string;
  url: string;
}

/** Manager coaching returned after productivity/workload live-data answers. */
export interface CoachingSuggestion {
  issue: string;
  approach: string;
  draftMessage: string;
  followUpActions: string[];
}

export type DecisionConfidenceLevel = 'high' | 'medium' | 'low' | 'insufficient';

export interface DecisionEvidence {
  tool: string;
  label: string;
  summary: string;
  ok: boolean;
  scope?: 'full' | 'partial' | 'empty';
}

export interface DecisionExplanation {
  summary: string;
  supportingEvidence: string[];
  contradictoryEvidence: string[];
  confidence: DecisionConfidenceLevel;
  type: string;
  rank: number;
}

export interface DecisionConfidence {
  level: DecisionConfidenceLevel;
  score: number;
  explanation: string;
  limitations: string[];
}

export interface DecisionSafeAction {
  type: 'navigate' | 'filter' | 'compare' | 'help';
  label: string;
  route?: string;
  params?: Record<string, string>;
  prompt?: string;
}

export interface DecisionCitation {
  title: string;
  url: string;
  source: 'help_center' | 'metric_dictionary';
}

export interface DecisionSuggestedApproach {
  title: string;
  body: string;
  personality: TempoPersonalityId;
}

  /** Structured decision intelligence from the Agent Gateway. */
export interface DecisionResponse {
  observation: string;
  evidence: DecisionEvidence[];
  explanations: DecisionExplanation[];
  confidence: DecisionConfidence;
  recommendation: string;
  recommendationType?: string;
  suggestedActions: DecisionSafeAction[];
  suggestedApproach?: DecisionSuggestedApproach;
  citations: DecisionCitation[];
  signals?: Array<{
    type: string;
    severity: 'info' | 'warning' | 'critical';
    metricId: string;
    detectedAt: string;
  }>;
  /** Deterministic cross-signal interpretations — the key insight lines. */
  correlations?: Array<{
    id: string;
    interpretation: string;
    severity: 'info' | 'warning';
  }>;
}

/** LLM token usage for a single answer. Now always present on gateway answers. */
export interface TokenUsage {
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  /** Billable AI-credit charge for the turn (gateway uniform pricing). */
  credits?: number;
}

/** How the gateway produced the answer (meta.answerSource). */
export type AgentAnswerSource =
  | 'llm'
  | 'llm_fallback_model'
  | 'deterministic'
  | 'error';

/** Soft daily/monthly token budget for Tempo Usage settings. */
export interface TokenQuotaBucket {
  used: number;
  limit: number;
  remaining: number;
  percentUsed: number;
  period: string;
  resetsAt: string;
}

/** Per-user attribution for today's company pool (gateway top-N). */
export interface TokenQuotaUserUsage {
  userId: string;
  tokens: number;
  /** Optional display name when the gateway already resolved it. */
  name?: string;
}

export interface TokenQuotaUsage {
  /** Shared company pool — all users burn the same budget. */
  scope?: 'company';
  daily: TokenQuotaBucket;
  monthly: TokenQuotaBucket;
  /** Top consumers today (UTC); empty when gateway omits attribution. */
  topUsersToday?: TokenQuotaUserUsage[];
}

/**
 * An in-app navigation shortcut resolved by the gateway (never model-generated).
 * Rendered as a chip that routes inside the app via Angular Router.
 */
export interface NavAction {
  label: string;
  route: string;
  params?: Record<string, unknown>;
  /** Optional Material icon name hint. */
  icon?: string;
}

/** Filtered report deep-link for short-format cards (default on from the gateway). */
export interface ReportLink {
  id: string;
  route: string;
  params?: Record<string, unknown>;
  label: string;
  phrases: string[];
  icon?: string;
}

export interface ShortFormatCard {
  disclaimer: string;
  visual: 'trend' | 'people' | 'none';
  /** Gateway: 2+ people and a chart is attached. Copilot toggles locally. */
  canShowTrend?: boolean;
  /** Gateway: 2+ people and a table is attached. Copilot toggles locally. */
  canShowPeople?: boolean;
}

/** Chart types the Agent Gateway may attach (see td-agent-gateway ChartSpec). */
export type ChartType =
  | 'bar'
  | 'line'
  | 'stacked_bar'
  | 'pie'
  | 'donut'
  | 'multi_series_bar'
  | 'multi_series_line';

export interface ChartSeries {
  key: string;
  label: string;
  values: number[];
  /** Pre-formatted display values aligned with values[] ("7h 32m"). */
  formattedValues?: string[];
}

/** Structured chart payload for Copilot to render (geometry is tool-derived). */
export interface ChartSpec {
  id: string;
  type: ChartType;
  title: string;
  /** Scope line under the title ("Engineering · Jun 1 – Jun 7"). */
  subtitle?: string;
  description?: string;
  unit?: string;
  categories: string[];
  series: ChartSeries[];
  /** Horizontal goal/baseline marker in the chart's unit. */
  referenceLine?: { value: number, label: string };
  sourceTool?: string;
}

/** Column type for a copilot table (see td-agent-gateway TableSpec). Drives sort behavior + display formatting. */
export type TableColumnType = 'text' | 'number' | 'duration' | 'percent';

export interface TableColumn {
  key: string;
  label: string;
  type: TableColumnType;
  sortable: boolean;
}

export interface TableSort {
  column: string;
  direction: 'asc' | 'desc';
}

/**
 * Structured multi-metric table payload for Copilot to render — the
 * counterpart to ChartSpec for per-row data with more columns than a chart
 * series can represent (e.g. hours + productive% + idle% per person).
 */
export interface TableSpec {
  id: string;
  title: string;
  subtitle?: string;
  columns: TableColumn[];
  /** Row values keyed by column.key. Duration columns carry raw seconds plus a `<key>Formatted` string; percent columns are 0-100. */
  rows: Record<string, unknown>[];
  defaultSort?: TableSort;
  sourceTool?: string;
}

/** Assistant persona chrome (e.g. Kevin). */
export interface CopilotPersona {
  id: string;
  displayName: string;
}

/** Live-data badge on an assistant message. */
export interface CopilotLiveDataBadge {
  active: boolean;
  label: string;
}

/** Server-normalized scope breadcrumb (“narrowed to Ramiro · Jul 9 – Jul 15”). */
export interface ConversationScope {
  narrowed: boolean;
  users?: Array<{ id: string, name: string }>;
  groups?: Array<{ id: string, name: string }>;
  dateRange?: { start: string, end: string };
  label?: string;
  /** Where the applied date range came from on this turn. */
  dateRangeSource?: 'message' | 'ui' | 'default';
  /** Brief note when the message period overrode the sticky UI filter. */
  dateRangeOverrideNote?: string;
}

/** Dig-into person CTA from the gateway. */
export interface PersonAction {
  userId: string;
  displayName: string;
  label: string;
  prompt: string;
  nav?: { route: string, params?: Record<string, string>, icon?: string };
}

/** App/usage pill chip. */
export interface UsageChip {
  label: string;
  hours: number;
  pct?: number;
  userId?: string;
}

export type RecommendationActionKind =
  | 'client_send_note'
  | 'client_skip'
  | 'client_ask_call';

export interface RecommendationAction {
  kind: RecommendationActionKind;
  label: string;
  primary?: boolean;
}

/** Kevin recommendation card — client executes Send/Skip (gateway is read-only). */
export interface RecommendationCard {
  title: string;
  body?: string;
  draftMessage: string;
  draftLabel?: string;
  actions: RecommendationAction[];
}

/** Simulated or real external check (e.g. Checked Slack). */
export interface ToolCheck {
  id: string;
  label: string;
  simulated: boolean;
}

/** One MCP/gateway tool query shown in the checkmark trace. */
export interface ToolStep {
  text: string;
  tool: string;
  done?: boolean;
}

/** SSE status payload from `/agent/chat/stream`. */
export interface AgentGatewayStatusEvent {
  state: string;
  tool?: string;
  label?: string;
  simulated?: boolean;
  /** Short-format chrome sent before the slow numeric rewrite finishes. */
  card?: {
    reportLinks?: unknown;
    shortFormat?: unknown;
    charts?: unknown;
    tables?: unknown;
    liveData?: unknown;
    conversationScope?: unknown;
    persona?: unknown;
  };
}

/** UI-facing message (adds id/timestamp/pending/error to the wire message). */
export interface CopilotUiMessage {
  id: string;
  role: CopilotMessageRole;
  content: string;
  createdAt: string;
  pending?: boolean;
  error?: boolean;
  actions?: CopilotAction[];
  /** Curated Help Center links to render as "Learn more" (external, open in a new tab). */
  helpfulLinks?: HelpLink[];
  /** Manager coaching: approach guidance + copy-ready draft message. */
  coachingSuggestions?: CoachingSuggestion[];
  /** Structured charts from the Agent Gateway (rendered as SVG). */
  charts?: ChartSpec[];
  /** Structured multi-metric tables from the Agent Gateway. */
  tables?: TableSpec[];
  /** UI-only notice (e.g. live-data connect/disconnect). Never sent to the agent as conversation. */
  transient?: boolean;
  /** Normalized tool output from the Agent Gateway. Populated in dev mode only (debugging aid). */
  sourceData?: unknown;
  /** LLM token usage for this answer (shown under the bubble). Absent for deterministic answers. */
  usage?: TokenUsage | null;
  /** Model that produced the answer (shown alongside token usage). */
  model?: string | null;
  /** Ask AI wire format for retry with @-mentions preserved. */
  wire?: string;
  /** Gateway clarification hint (`date_range` | `user` | `team`). */
  clarification?: 'date_range' | 'user' | 'team' | null;
  /** Structured decision intelligence (when Decision Engine is enabled). */
  decision?: DecisionResponse;
  /** Gateway request id for feedback correlation. */
  requestId?: string;
  /** Gateway tool that produced this answer (e.g. live_workspace_data). */
  tool?: string | null;
  /** User feedback on this answer (UI state). */
  feedbackRating?: 'helpful' | 'not_helpful';
  /** Assistant persona (Kevin). */
  persona?: CopilotPersona;
  /** Live-data badge from gateway. */
  liveData?: CopilotLiveDataBadge;
  /** Narrowed person/group/date breadcrumb. */
  conversationScope?: ConversationScope;
  /** Dig-into person CTAs. */
  peopleActions?: PersonAction[];
  /** App/usage pills. */
  usageChips?: UsageChip[];
  /** Recommendation card with editable draft + client intents. */
  recommendationCard?: RecommendationCard;
  /** External / POC tool checks (e.g. Checked Slack). */
  toolChecks?: ToolCheck[];
  /** Tools queried for this answer (Ask AI checkmark trace). */
  toolSteps?: ToolStep[];
  /** Progressive SSE status label while pending. */
  statusLabel?: string | null;
  /** How the gateway produced the answer; 'deterministic' renders a degraded note. */
  answerSource?: AgentAnswerSource | null;
  /** False → permanent failure for now; hide retry affordances. */
  retryable?: boolean | null;
  /** Scope provenance chip text (stripped from the answer tail). */
  scopeEcho?: string | null;
  /** Gateway caveat strings (result_truncated, unverified figures, …). */
  caveats?: string[];
  /** Non-clickable tip under the answer (e.g. rephrase / @-mention guidance). */
  softHint?: string;
  /** Filtered report deep-links for short-format inline pills. */
  reportLinks?: ReportLink[];
  /** Short-format card chrome (disclaimer + which visual was sent). */
  shortFormat?: ShortFormatCard;
}

/** One Tempo chat tab (local id + optional gateway conversationId + UI messages). */
export interface CopilotConversationTab {
  id: string;
  conversationId?: string;
  title: string;
  updatedAt: string;
  messages: CopilotUiMessage[];
}

/** Persisted multi-conversation layout. */
export interface CopilotConversationsStore {
  activeId: string;
  conversations: CopilotConversationTab[];
}

// --- New NestJS Agent Gateway (POST /agent/chat) ---------------------------

/** Page-aware context sent to the Agent Gateway with each chat request. */
export interface AgentGatewayContext {
  route?: string;
  companyId?: string;
  userId?: string;
  role?: string;
  dateRange?: { from?: string, to?: string };
  timezone?: string;
  filters?: Record<string, unknown>;
  plan?: string;
  featureFlags?: Record<string, boolean | string | string[]>;
}

/** @-selection payload from the mention composer (maps to gateway filters). */
export interface CopilotMentionSelection {
  selectedUsers?: Array<{ id: string, name?: string, timezone?: string }>;
  selectedGroups?: Array<{ id: string, name?: string }>;
}

/** Optional send payload when the mention composer is used. */
export interface CopilotSendOptions {
  /** Ask AI wire string for accurate retry (`@Name<user:id>`). */
  wire?: string;
  /** Human-readable bubble text. */
  displayText?: string;
  /** Gateway message with `@[Name](user:id)` tokens. */
  gatewayMessage?: string;
  selection?: CopilotMentionSelection;
  /** Merged into the gateway context (e.g. insight card metadata). */
  contextOverrides?: Partial<AgentGatewayContext>;
}

/** In-panel date range / timezone overrides (merged into gateway context). */
export interface CopilotPanelScope {
  dateRange?: { from: string, to: string };
  timezone?: string;
}

/** Tempo response style presets (maps to gateway `featureFlags.tempoPersonality`). */
export type TempoPersonalityId = 'manager_coach' | 'executive_brief' | 'analyst_detailed';

/** Named advisor cast (maps to gateway `featureFlags.advisorPersona`). */
export type AdvisorPersonaId = 'kevin' | 'maya' | 'alex' | 'sam';

/** Client-side Tempo intelligence preferences sent with each gateway request. */
export interface CopilotAgentSettings {
  /** Preferred named advisor; drives chrome voice and tempoPersonality bridge. */
  advisorPersona: AdvisorPersonaId;
  /** Legacy style bridge still sent for older gateway builds. */
  tempoPersonality: TempoPersonalityId;
  /** When true, sends `featureFlags.decisionEngine` to opt into the decision path. */
  decisionEngineEnabled: boolean;
  /** When true, sends `featureFlags.agentLongNarrative` for archetype / outlier prose. */
  agentLongNarrativeEnabled: boolean;
}

export type CopilotClarification = 'date_range' | 'user' | 'team';

export interface AgentGatewayRequest {
  message: string;
  context: AgentGatewayContext;
  /** Opaque conversation id for multi-turn memory. Echo back the value the gateway returned. */
  conversationId?: string;
  /**
   * Retry-safety key sent as the `Idempotency-Key` header (not in the body).
   * Mint one UUID per logical send and reuse it on retries — the gateway
   * replays the finished answer instead of burning a second LLM turn.
   */
  idempotencyKey?: string;
}

/** Raw response body from the Agent Gateway (see td-agent-gateway ChatResponseDto). */
export interface AgentGatewayResponseBody {
  answer: string;
  /** Opaque conversation id to persist and resend on the next turn (multi-turn memory). */
  conversationId?: string;
  sourceData?: unknown;
  suggestedActions?: unknown;
  helpfulLinks?: unknown;
  coachingSuggestions?: unknown;
  /** In-app navigation shortcuts (route chips). */
  navActions?: unknown;
  /** Filtered report deep-links (short-format cards). */
  reportLinks?: unknown;
  /** Short-format card chrome. */
  shortFormat?: unknown;
  /** Structured charts for Copilot to render (AGENT_CHARTS_ENABLED). */
  charts?: unknown;
  /** Structured multi-metric tables for Copilot to render (AGENT_TABLES_ENABLED). */
  tables?: unknown;
  decision?: unknown;
  persona?: unknown;
  liveData?: unknown;
  conversationScope?: unknown;
  peopleActions?: unknown;
  usageChips?: unknown;
  recommendationCard?: unknown;
  toolSteps?: unknown;
  meta?: {
    tool?: string | null;
    generatedBy?: string;
    model?: string | null;
    scope?: unknown;
    sources?: unknown;
    caveats?: unknown;
    error?: string | null;
    clarification?: 'date_range' | 'user' | 'team' | null;
    /** How the answer was produced ('deterministic' → LLM was unavailable). */
    answerSource?: unknown;
    /** False → permanent failure (quota/not configured); no retry affordance. */
    retryable?: unknown;
    /** Provenance line ("Based on Idle Time report · …") duplicated from the answer tail. */
    scopeEcho?: unknown;
    /** Token + credits usage; now always present on gateway answers. */
    usage?: {
      promptTokens?: unknown;
      completionTokens?: unknown;
      totalTokens?: unknown;
      credits?: unknown;
    } | null;
    /** Correlation id for feedback on this turn. */
    requestId?: string;
    narrativeMode?: 'short' | 'long';
    toolChecks?: unknown;
  };
  debug?: {
    requestId?: string;
  };
}

/** Normalized, transport-level failure categories surfaced to the UI. */
export type AgentGatewayErrorKind = 'unauthorized' | 'timeout' | 'unavailable' | 'malformed';

/** Discriminated result returned by AgentGatewayService (never throws). */
export type AgentGatewayResult =
  | {
      status: 'success';
      answer: string;
      suggestedActions: string[];
      helpfulLinks: HelpLink[];
      coachingSuggestions: CoachingSuggestion[];
      /** In-app navigation shortcuts (route chips). */
      navActions: NavAction[];
      reportLinks?: ReportLink[];
      shortFormat?: ShortFormatCard | null;
      /** Structured charts for in-panel rendering. */
      charts: ChartSpec[];
      /** Structured multi-metric tables for in-panel rendering. */
      tables: TableSpec[];
      /** Structured decision intelligence payload. */
      decision?: DecisionResponse | null;
      sourceData?: unknown;
      /** True when the gateway matched no tool (unsupported intent). */
      unsupported: boolean;
      tool: string | null;
      /** Model that produced the answer, when an LLM was involved. */
      model: string | null;
      /** LLM token usage for the answer; null for deterministic answers. */
      usage: TokenUsage | null;
      /** Conversation id to persist for the next turn (multi-turn memory). */
      conversationId?: string;
      /** Correlation id for feedback on this turn. */
      requestId?: string;
      clarification?: 'date_range' | 'user' | 'team' | null;
      /** Gateway tool error code when the answer is an error fallback (not transport failure). */
      gatewayError?: string | null;
      persona?: CopilotPersona | null;
      liveData?: CopilotLiveDataBadge | null;
      conversationScope?: ConversationScope | null;
      peopleActions?: PersonAction[];
      usageChips?: UsageChip[];
      recommendationCard?: RecommendationCard | null;
      toolChecks?: ToolCheck[];
      toolSteps?: ToolStep[];
      /** How the gateway produced the answer (degraded-mode signal). */
      answerSource?: AgentAnswerSource | null;
      /** False → permanent for now; suppress retry affordances. */
      retryable?: boolean | null;
      /** Provenance line for the scope chip (also the answer's last paragraph). */
      scopeEcho?: string | null;
      /** User-relevant caveat strings (e.g. result_truncated). */
      caveats?: string[];
    }
  | {
      status: 'error';
      errorKind: AgentGatewayErrorKind;
      /** Human-readable message from the gateway error envelope, when available. */
      message?: string;
      /** Correlation id for support (error envelope / X-Request-Id). */
      requestId?: string;
      /** False → do not offer retry. */
      retryable?: boolean;
    };

/**
 * Streaming callbacks for {@link AgentGatewayService.streamMessage}. `onToken` fires for each
 * incremental chunk of the answer so the UI can render progressively; the resolved
 * {@link AgentGatewayResult} carries the final, authoritative payload (actions, links, meta).
 */
export interface AgentGatewayStreamHandlers {
  onConversationId?: (conversationId: string) => void;
  onToken?: (chunk: string, answerSoFar: string) => void;
  onStatus?: (event: AgentGatewayStatusEvent) => void;
  onCard?: (card: AgentGatewayCardPreview) => void;
}

/** Mid-stream short-format card so Copilot can paint toggles before `final`. */
export interface AgentGatewayCardPreview {
  reportLinks?: ReportLink[];
  shortFormat?: ShortFormatCard | null;
  charts?: ChartSpec[];
  tables?: TableSpec[];
  liveData?: CopilotUiMessage['liveData'];
  conversationScope?: ConversationScope;
  persona?: CopilotUiMessage['persona'];
}
